import type { NavItem } from "./navItems";

/**
 * The sidebar tabs a firm may hide (Settings > General > Sidebar), by their
 * `NavItem` id, in sidebar order. The backend keeps the same list
 * (`law_firm_service.SIDEBAR_TABS`) and drops ids it doesn't know. Settings
 * lives in the account menu, never the sidebar, so it can't be hidden.
 */
export const SIDEBAR_TABS = [
  { id: "home", label: "Home" },
  { id: "calendar", label: "Calendar" },
  { id: "cases", label: "Cases" },
  { id: "clients", label: "Clients" },
  { id: "companies", label: "Companies" },
  { id: "time", label: "Time" },
  { id: "billing-review", label: "Billing Review" },
  { id: "firm-knowledge", label: "Firm Knowledge" },
  { id: "automations", label: "Automations" },
  { id: "connectors", label: "Connectors" },
  { id: "athena", label: "Athena" }
] as const;

export type SidebarTab = (typeof SIDEBAR_TABS)[number];
export type SidebarTabId = SidebarTab["id"];

/** Shown only with time tracking, to admins, or with Athena access. */
const CONDITIONAL_TABS: ReadonlySet<string> = new Set([
  "time",
  "billing-review",
  "athena"
]);

/** A tab every lawyer at the firm sees; at least one of them stays visible. */
export const isCoreSidebarTab = (id: string): boolean =>
  !CONDITIONAL_TABS.has(id);

/** False when hiding `id` would leave no tab everyone sees. */
export const canHideSidebarTab = (
  hidden: readonly string[],
  id: string
): boolean =>
  !isCoreSidebarTab(id) ||
  SIDEBAR_TABS.some(
    (tab) =>
      tab.id !== id && isCoreSidebarTab(tab.id) && !hidden.includes(tab.id)
  );

/** `hidden` with `id` shown or hidden, in sidebar order. */
export const withSidebarTab = (
  hidden: readonly string[],
  id: string,
  show: boolean
): string[] => {
  const next = new Set(hidden);
  if (show) next.delete(id);
  else next.add(id);
  return SIDEBAR_TABS.map((tab) => tab.id).filter((tabId) => next.has(tabId));
};

/** `items` without the firm's hidden tabs; never empties the sidebar. */
export const hideSidebarTabs = (
  items: NavItem[],
  hidden: readonly string[]
): NavItem[] => {
  if (!hidden.length) return items;
  const shown = items.filter((item) => !hidden.includes(item.id));
  return shown.length ? shown : items;
};
